import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Sparkles, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, streamSSE } from "@/lib/api";

const ModeSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="mt-2 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="replace">Replace current sections</SelectItem>
      <SelectItem value="append">Add after current sections</SelectItem>
    </SelectContent>
  </Select>
);

const LiveFeed = ({ live }) => (
  <div className="bg-[#38171E] text-[#EDE3C8] p-5 mt-6" data-testid="ai-live-feed">
    <div className="flex items-center justify-between text-[10px] tracking-[0.28em] text-[#D4AF37]">
      <span>THE PRESS IS WRITING</span>
      <span data-testid="ai-progress">{live.done} done</span>
    </div>
    <div className="font-display text-2xl text-[#F3E5AB] mt-3" data-testid="ai-live-heading">{live.heading || live.status}</div>
    <div className="ai-stream mt-3 max-h-56 overflow-y-auto text-[#EDE3C8]/85">{live.text.slice(-1800).replace(/<[^>]+>/g, " ")}</div>
  </div>
);

export const AIPanel = ({ product, ensureSaved, onProgress, onDone, onStarted }) => {
  const nav = useNavigate();
  const [w, setW] = useState({ topic: "", audience: "", notes: "", sections_count: "6", length: "medium", mode: product.sections?.length ? "append" : "replace" });
  const [c, setC] = useState({ text: "", instructions: "", mode: "append", target: product.sections?.length ? "new" : "this" });
  const [file, setFile] = useState(null);
  const [live, setLive] = useState(null);
  const ctrl = useRef(null);
  const fileRef = useRef(null);

  const handle = (ev) => {
    if (ev.type === "status") setLive((l) => ({ ...l, status: ev.message }));
    if (ev.type === "outline") onProgress();
    if (ev.type === "section_start") setLive((l) => ({ ...l, heading: ev.heading, text: "" }));
    if (ev.type === "delta") setLive((l) => ({ ...l, text: l.text + ev.text }));
    if (ev.type === "section_done" || ev.type === "sections_added") {
      setLive((l) => ({ ...l, done: l.done + (ev.sections?.length || 1) }));
      onProgress();
    }
    if (ev.type === "error") toast.error(ev.message);
    if (ev.type === "done") {
      toast.success("The manuscript is ready");
      onDone?.();
    }
  };

  const run = async (path, opts) => {
    try {
      await ensureSaved();
      ctrl.current = new AbortController();
      setLive({ status: "Starting", heading: "", text: "", done: 0 });
      await streamSSE(path, { ...opts, signal: ctrl.current.signal, onEvent: handle });
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setLive(null);
      onProgress();
    }
  };

  const write = () => {
    if (!w.topic.trim()) return toast.error("Describe the topic first");
    run(`/products/${product.id}/ai/write`, { body: { ...w, sections_count: Number(w.sections_count) } });
  };

  const job = product.ai_job || {};
  const converting = job.status === "running";
  const [uploading, setUploading] = useState(false);

  const convert = async () => {
    if (!file && c.text.trim().length < 40) return toast.error("Upload a file or paste at least a paragraph");
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("text", c.text);
    fd.append("instructions", c.instructions);
    fd.append("mode", c.target === "this" && !product.sections?.length ? "replace" : c.mode);
    fd.append("target", c.target);
    if (c.target === "this" && c.mode === "replace" && product.sections?.length &&
      !window.confirm(`This replaces all ${product.sections.length} sections of “${product.title}”. Continue?`)) return;
    setUploading(true);
    try {
      await ensureSaved();
      const r = await api.post(`/products/${product.id}/ai/convert`, fd);
      toast.success(`Converting ${r.data.total} part${r.data.total === 1 ? "" : "s"} into “${r.data.title}” — you can leave and come back`);
      if (r.data.product_id !== product.id) return nav(`/products/${r.data.product_id}`);
      onStarted(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const busy = !!live || converting || uploading;


  return (
    <div className="bg-[#FFFDF9] border border-[#2A2325]/10 p-6" data-testid="ai-panel">
      <Tabs defaultValue="write">
        <TabsList className="rounded-none bg-[#F5EFE6]">
          <TabsTrigger value="write" className="rounded-none" data-testid="ai-tab-write">Write from a topic</TabsTrigger>
          <TabsTrigger value="convert" className="rounded-none" data-testid="ai-tab-convert">Convert my draft</TabsTrigger>
        </TabsList>
        <TabsContent value="write" className="space-y-4 pt-4">
          <div>
            <Label>What is it about?</Label>
            <Textarea rows={3} value={w.topic} onChange={(e) => setW({ ...w, topic: e.target.value })} placeholder="e.g. How to read Michelangelo's David up close: the marble, the politics, the hands" className="mt-2 bg-white" data-testid="ai-write-topic-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Reader</Label>
              <Input value={w.audience} onChange={(e) => setW({ ...w, audience: e.target.value })} placeholder="e.g. curious first-time visitors" className="mt-2 bg-white" data-testid="ai-write-audience-input" />
            </div>
            <div>
              <Label>Sections</Label>
              <Select value={w.sections_count} onValueChange={(v) => setW({ ...w, sections_count: v })}>
                <SelectTrigger className="mt-2 bg-white" data-testid="ai-write-sections-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Length per section</Label>
              <Select value={w.length} onValueChange={(v) => setW({ ...w, length: v })}>
                <SelectTrigger className="mt-2 bg-white" data-testid="ai-write-length-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (~700–1,000 words per chapter)</SelectItem>
                  <SelectItem value="medium">Medium (~1,100–1,600 words per chapter)</SelectItem>
                  <SelectItem value="long">Long (~1,800–2,600 words per chapter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Existing sections</Label>
              <ModeSelect value={w.mode} onChange={(v) => setW({ ...w, mode: v })} testId="ai-write-mode-select" />
            </div>
          </div>
          <div>
            <Label>Notes, facts or must-include points (optional)</Label>
            <Textarea rows={3} value={w.notes} onChange={(e) => setW({ ...w, notes: e.target.value })} className="mt-2 bg-white" data-testid="ai-write-notes-input" />
          </div>
          <Button onClick={write} disabled={busy} className="rounded-none gap-2" data-testid="ai-write-start-btn"><Sparkles size={15} /> Write it</Button>
        </TabsContent>
        <TabsContent value="convert" className="space-y-4 pt-4">
          <div>
            <Label>Upload your draft (PDF, TXT, MD or HTML)</Label>
            <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.html,.htm" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ai-convert-file-input" />
            <button onClick={() => fileRef.current.click()} className="mt-2 w-full border border-dashed border-[#5E2B38]/40 bg-[#FAF7F0] py-6 flex flex-col items-center gap-2 text-sm text-[#5C5255] hover:border-[#5E2B38] transition-colors" data-testid="ai-convert-file-btn">
              <FileUp size={20} className="text-[#5E2B38]" />
              {file ? <span className="text-[#2A2325]" data-testid="ai-convert-file-name">{file.name}</span> : "Choose a file"}
            </button>
          </div>
          <div>
            <Label>…or paste text</Label>
            <Textarea rows={5} value={c.text} onChange={(e) => setC({ ...c, text: e.target.value })} className="mt-2 bg-white" data-testid="ai-convert-text-input" />
          </div>
          <div>
            <Label>Convert into</Label>
            <Select value={c.target} onValueChange={(v) => setC({ ...c, target: v })}>
              <SelectTrigger className="mt-2 bg-white" data-testid="ai-convert-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">A new title — AI reads its title & picks the category</SelectItem>
                <SelectItem value="this">This title: {product.title.slice(0, 60)}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#5C5255] mt-1.5" data-testid="ai-convert-target-hint">
              {c.target === "new" ? "Best when the draft is a different book or guide. This title stays untouched." : "Use this only when the draft is this same book."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Extra instructions (optional)</Label>
              <Input value={c.instructions} onChange={(e) => setC({ ...c, instructions: e.target.value })} placeholder="e.g. keep all dates exactly" className="mt-2 bg-white" data-testid="ai-convert-instructions-input" />
            </div>
            {c.target === "this" && product.sections?.length > 0 && (
              <div>
                <Label>Existing sections</Label>
                <ModeSelect value={c.mode} onChange={(v) => setC({ ...c, mode: v })} testId="ai-convert-mode-select" />
              </div>
            )}
          </div>
          <Button onClick={convert} disabled={busy} className="rounded-none gap-2" data-testid="ai-convert-start-btn"><Sparkles size={15} /> {uploading ? "Reading your file…" : "Convert to house style"}</Button>
          {converting && <p className="text-sm text-[#5C5255] italic" data-testid="ai-convert-running-note">Converting — progress is shown at the top of this page.</p>}
          {job.status === "failed" && job.kind === "convert" && <p className="text-sm text-[#9B2C2C]" data-testid="ai-convert-error">Last conversion stopped: {job.error}</p>}
          {job.status === "done" && job.kind === "convert" && <p className="text-sm text-[#2D5A3F]" data-testid="ai-convert-done">Last conversion added {job.added} sections.</p>}
        </TabsContent>
      </Tabs>
      {live && (
        <>
          <LiveFeed live={live} />
          <Button variant="outline" size="sm" className="rounded-none mt-3 gap-1.5" onClick={() => ctrl.current?.abort()} data-testid="ai-stop-btn"><Square size={12} /> Stop</Button>
        </>
      )}
    </div>
  );
};
